import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { computeFloatingPosition } from '../floating-position.util';
import { TourService, VwTourStep } from './tour.service';

// A single overlay instance lives in the app shell (default-layout) for the
// whole session; it renders nothing until TourService.start() is called.
@Component({
  selector: 'app-tour-overlay',
  templateUrl: './tour-overlay.component.html',
  styleUrls: ['./tour-overlay.component.scss'],
})
export class TourOverlayComponent implements OnInit, OnDestroy {
  steps: VwTourStep[] = [];
  index = 0;

  spotlightTop = 0;
  spotlightLeft = 0;
  spotlightWidth = 0;
  spotlightHeight = 0;
  cardTop = 0;
  cardLeft = 0;

  private subs: Subscription[] = [];
  private resizeListener = () => this.reposition();

  constructor(public tour: TourService) {}

  get currentStep(): VwTourStep | null {
    return this.steps[this.index] || null;
  }

  ngOnInit(): void {
    this.subs.push(this.tour.steps$.subscribe(steps => {
      this.steps = steps || [];
      if (this.steps.length) {
        window.addEventListener('resize', this.resizeListener);
        window.addEventListener('scroll', this.resizeListener, true);
      } else {
        window.removeEventListener('resize', this.resizeListener);
        window.removeEventListener('scroll', this.resizeListener, true);
      }
    }));
    this.subs.push(this.tour.index$.subscribe(index => {
      this.index = index;
      setTimeout(() => this.reposition(), 30);
    }));
  }

  private reposition(): void {
    const step = this.currentStep;
    if (!step) {
      return;
    }
    const target = this.tour.resolveTarget(step.key);
    if (!target) {
      // The element that anchored this step vanished (e.g. a role-gated
      // panel closed) — skip forward rather than spotlighting nothing.
      this.tour.next();
      return;
    }
    target.scrollIntoView({ block: 'center', behavior: 'smooth' });
    const rect = target.getBoundingClientRect();
    const pad = 6;
    this.spotlightTop = rect.top - pad;
    this.spotlightLeft = rect.left - pad;
    this.spotlightWidth = rect.width + pad * 2;
    this.spotlightHeight = rect.height + pad * 2;

    const result = computeFloatingPosition(rect, 300, 160, 'auto');
    this.cardTop = result.top;
    this.cardLeft = result.left;
  }

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (!this.tour.isActive) {
      return;
    }
    if (event.key === 'Escape') {
      this.tour.skip();
    } else if (event.key === 'ArrowRight') {
      this.tour.next();
    } else if (event.key === 'ArrowLeft') {
      this.tour.back();
    }
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
    window.removeEventListener('resize', this.resizeListener);
    window.removeEventListener('scroll', this.resizeListener, true);
  }
}
